import { Injectable } from '@angular/core';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { environment } from 'src/environments/environment';
import Swal from 'sweetalert2';
import { ErrorHttpReal, errorHttpReal } from 'src/app/shared/utils/error-http-real';

/**
 * Candado de producción de los paneles de administración de tasas.
 *
 * En producción, las acciones de escritura de esos paneles quedan cerradas
 * para todos los operadores salvo los correos de esta lista. En qa y
 * desarrollo no aplica: ahí edita cualquier operador.
 *
 * Alcanza a: el home de operador (`<app-main-rates>`), `cripto-rate`,
 * `set-rates-parameters`, `set-rates-sales-parameters`,
 * `set-rates-parameters-bolivares` y `comisiones-bancarias`.
 *
 * Quedan FUERA del candado, para cualquier operador y en todos los ambientes,
 * los toggles de rieles de "Pagos Venezuela" (N58 / Banco Plaza / R4 Conecta).
 *
 * OJO: esto es un candado de UI. Los endpoints siguen aceptando a cualquier
 * operador autenticado; el candado duro tiene que vivir en backend.
 *
 * Decisión del dueño (22/10): en prod editan los mismos 4 correos en todas
 * las listas (esta, `EDITORES_TASAS_PROD` y las del back).
 */
export const OPERADORES_EDICION_ADMIN: readonly string[] = [
  'carlos@aguacatecambios.com',
  'carlos@aguacatewallet.com',
  'edward@aguacatewallet.com',
  'andres@aguacatecambios.com',
];

/**
 * Editores de TASAS en producción: los únicos que pueden abrir y guardar los
 * editores de franjas CLP→VES, VES→CLP y tramos VES→USDT. Es una lista aparte
 * de `OPERADORES_EDICION_ADMIN` (que no cambia). El back también la exige: en
 * prod, `PUT rates/franjas/:par` y `PUT rates/tramos-usdt/ves-usdt` responden
 * 403 si el correo no está acá.
 */
export const EDITORES_TASAS_PROD: readonly string[] = [
  'carlos@aguacatecambios.com',
  'carlos@aguacatewallet.com',
  'edward@aguacatewallet.com',
  'andres@aguacatecambios.com',
];

/** Motivo visible junto a los controles de tasas bloqueados en prod. */
export const TEXTO_SIN_PERMISO_TASAS = 'Solo pueden editar los encargados de tasas.';

/**
 * Aviso de un error al escribir parámetros de tasas (`POST /parametro`,
 * `PUT /conversionRate`). Esas llamadas van con `{ errorReal: true }`
 * (escape del `ErrorInterceptor`), así que el status llega intacto y la
 * decisión es determinista:
 *  - 403 (no es encargado de tasas): el mensaje del servidor, como aviso.
 *  - Cualquier otro: el mensaje del servidor o `fallback`, como error.
 * Devuelve el error traducido para que el llamador decida qué más hacer
 * (cerrar el diálogo, volver el control a lo guardado).
 */
export async function avisarErrorTasas(err: any, fallback: string): Promise<ErrorHttpReal> {
  const e = errorHttpReal(err, fallback, { conEscape: true });
  if (e.status === 401) {
    // Con el escape el ErrorInterceptor no revisa la sesión. Se recarga la
    // página: si la sesión venció, los guards llevan al login; si seguía
    // viva (401 transitorio del back), el operador vuelve a la misma pantalla.
    await Swal.fire({
      icon: 'warning',
      title: 'Tu sesión venció',
      text: 'Vuelve a iniciar sesión para guardar el cambio.',
      confirmButtonText: 'Continuar',
      confirmButtonColor: '#1e5a3f',
      allowOutsideClick: false,
    });
    window.location.reload();
  } else if (e.status === 403) {
    await Swal.fire({
      icon: 'warning',
      title: 'Acción no disponible',
      text: e.message,
      confirmButtonText: 'Entendido',
      confirmButtonColor: '#f59e0b',
    });
  } else {
    await Swal.fire({
      icon: 'error',
      title: 'No se pudo guardar',
      text: e.message,
      confirmButtonColor: '#1e5a3f',
    });
  }
  return e;
}

/**
 * `environment.production` no sirve para distinguir prod: está en `true`
 * también en `environment.development.ts` (que apunta a la API de qa). El
 * único valor que es exclusivo del ambiente productivo es el stage del API
 * Gateway.
 */
export function esProduccion(): boolean {
  return /\/prod\/?$/.test(environment.apiGatewayURL ?? '');
}

/** True si el correo puede escribir. Fuera de producción devuelve siempre true. */
export function correoAutorizado(email: string | null | undefined): boolean {
  if (!esProduccion()) return true;
  if (!email) return false;
  return OPERADORES_EDICION_ADMIN.includes(email.trim().toLowerCase());
}

/** True si el correo puede editar tasas (franjas y tramos). Fuera de producción, siempre true. */
export function correoEditaTasas(email: string | null | undefined): boolean {
  if (!esProduccion()) return true;
  if (!email) return false;
  return EDITORES_TASAS_PROD.includes(email.trim().toLowerCase());
}

@Injectable({ providedIn: 'root' })
export class AdminEditPermissionsService {
  /**
   * Valor con el que arrancan los paneles antes de que Cognito responda. En
   * producción arranca cerrado para que no haya una ventana de botones vivos
   * mientras se resuelve el correo.
   */
  readonly valorInicial = !esProduccion();

  constructor(private cognitoService: CognitoService) {}

  /**
   * Resuelve si el operador logueado puede escribir. No cachea nada propio:
   * `CognitoService.getUser()` ya trae su propio cache con TTL.
   */
  async puedeEditar(): Promise<boolean> {
    try {
      const user: any = await this.cognitoService.getUser();
      return correoAutorizado(user?.attributes?.email);
    } catch {
      // Sin correo no hay excepción posible: en prod queda cerrado.
      return this.valorInicial;
    }
  }

  /**
   * Igual que `puedeEditar`, pero con la lista de editores de tasas
   * (`EDITORES_TASAS_PROD`). Se consulta fresco cada vez que se abre un editor.
   */
  async puedeEditarTasas(): Promise<boolean> {
    try {
      const user: any = await this.cognitoService.getUser();
      return correoEditaTasas(user?.attributes?.email);
    } catch {
      return this.valorInicial;
    }
  }

  /**
   * Guarda de escritura. El `[disabled]` del template ya bloquea el click;
   * esto cubre el caso de que alguien lo saque desde devtools. Devuelve true
   * cuando hay que abortar la acción.
   */
  bloqueado(
    puedeEditar: boolean,
    mensaje = 'Tu usuario no tiene permisos para modificar esta configuración.',
  ): boolean {
    if (puedeEditar) return false;

    Swal.fire({
      icon: 'warning',
      title: 'Acción no disponible',
      text: mensaje,
      confirmButtonText: 'Entendido',
      confirmButtonColor: '#f59e0b',
    });
    return true;
  }
}
